import clsx from 'clsx';
import type { ReactElement, ReactNode } from 'react';

export interface TabItem<T extends string = string> {
  id: T;
  label: string;
  /** Rendered as a muted pill after the label */
  count?: number;
  icon?: ReactNode;
  /** `warning` flags a tab needing attention, e.g. a zone holding orphaned cards */
  tone?: 'default' | 'warning';
  tooltip?: string;
  disabled?: boolean;
}

export interface TabsProps<T extends string = string> {
  tabs: TabItem<T>[];
  activeId: T;
  onChange: (id: T) => void;
  /** `underline` for in-page section switching, `pill` for filter bars */
  variant?: 'underline' | 'pill';
  size?: 'sm' | 'md';
  /** Accessible name for the tablist */
  label: string;
  className?: string;
}

const sizeStyles = {
  underline: { sm: 'px-3 py-2 text-sm', md: 'px-4 py-2.5 text-sm' },
  pill: { sm: 'px-2.5 py-1 text-xs', md: 'px-3 py-1.5 text-sm' },
};

function tabStyles(variant: 'underline' | 'pill', tone: 'default' | 'warning', isActive: boolean): string {
  if (variant === 'underline') {
    if (tone === 'warning') {
      return isActive ? 'border-gold text-gold' : 'text-gold/70 hover:text-gold border-transparent';
    }
    return isActive ? 'border-aether text-aether' : 'text-ash hover:text-silver hover:border-ash/50 border-transparent';
  }

  if (tone === 'warning') {
    return isActive ? 'bg-gold/20 text-gold' : 'text-gold/70 hover:bg-gold/10 hover:text-gold';
  }
  return isActive ? 'bg-storm text-pearl' : 'text-ash hover:bg-storm/50 hover:text-silver';
}

function countStyles(tone: 'default' | 'warning', isActive: boolean): string {
  if (tone === 'warning') {
    return isActive ? 'bg-gold/25 text-gold' : 'bg-gold/10 text-gold/70';
  }
  return isActive ? 'bg-aether/20 text-aether' : 'bg-storm/60 text-ash';
}

export default function Tabs<T extends string = string>({
  tabs,
  activeId,
  onChange,
  variant = 'underline',
  size = 'md',
  label,
  className,
}: TabsProps<T>): ReactElement {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={clsx(variant === 'underline' ? 'border-storm flex gap-1 border-b' : 'bg-slate/60 flex gap-1 rounded-lg p-1', className)}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeId;
        const tone = tab.tone ?? 'default';

        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={tab.disabled}
            title={tab.tooltip}
            onClick={() => onChange(tab.id)}
            className={clsx(
              'focus-ring flex items-center gap-2 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40',
              variant === 'underline' ? 'border-b-2' : 'rounded-md',
              sizeStyles[variant][size],
              tabStyles(variant, tone, isActive),
            )}
          >
            {tab.icon}
            {tab.label}
            {tab.count !== undefined && (
              <span className={clsx('rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums', countStyles(tone, isActive))}>
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
